import type React from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { AudioDevice } from "../hooks/useAudioDevices";

import { PopoverCloseButton, PopoverContainer, PopoverTitle } from "./PopoverStyles";

const WidePopoverContainer = styled(PopoverContainer)`
  min-width: 300px;
`;

interface Props {
  audioInputs: AudioDevice[];
  audioOutputs: AudioDevice[];
  selectedInputId: string;
  selectedOutputId: string;
  onInputChange: (deviceId: string) => void;
  onOutputChange: (deviceId: string) => void;
  onClose: () => void;
}

export const AudioDeviceSelector: React.FC<Props> = ({
  audioInputs,
  audioOutputs,
  selectedInputId,
  selectedOutputId,
  onInputChange,
  onOutputChange,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <WidePopoverContainer>
      <PopoverCloseButton onClick={onClose}>&times;</PopoverCloseButton>
      <PopoverTitle>{t("videocall.audio_settings.title")}</PopoverTitle>

      <Section>
        <Label>{t("videocall.audio_settings.microphone")}</Label>
        <Select value={selectedInputId} onChange={e => onInputChange(e.target.value)}>
          {audioInputs.map(device => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label}
            </option>
          ))}
        </Select>
      </Section>

      {audioOutputs.length > 0 && (
        <Section>
          <Label>{t("videocall.audio_settings.speaker")}</Label>
          <Select value={selectedOutputId} onChange={e => onOutputChange(e.target.value)}>
            {audioOutputs.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label}
              </option>
            ))}
          </Select>
        </Section>
      )}
    </WidePopoverContainer>
  );
};

const Section = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #4a90e2;
    background: rgba(255, 255, 255, 0.15);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;
